import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Bell, Check, AlertCircle, Info, AlertTriangle, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Clean notification links - handle various link formats
const cleanNotificationLink = (link) => {
  if (!link) return '/';
  
  // If it's /admin/funds/{uuid}, redirect to /admin/clients (funds page removed)
  if (link.match(/^\/admin\/funds/i)) {
    return '/admin/clients';
  }
  
  return link;
};

export default function NotificationsPage() {
  const { token, user } = useAuth();
  const isMirror = sessionStorage.getItem('mirrorMode') === 'true';
  const canDelete = isMirror || ['super_admin', 'admin_ops', 'bureau', 'analyst'].includes(user?.role);
  const { t } = useLanguage();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, [token]);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNotifications();
      // Signal the header badge to refresh
      window.dispatchEvent(new Event('notifications-updated'));
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'critical': return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'important': return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      default: return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-3xl">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
              {t('notifications')}
            </h2>
            {unreadCount > 0 && (
              <p className="text-sm text-muted-foreground">
                {unreadCount} non lue{unreadCount > 1 ? 's' : ''}
              </p>
            )}
          </div>
          {unreadCount > 0 && (
            <Button variant="outline" onClick={handleMarkAllAsRead} data-testid="mark-all-read-btn">
              <Check className="w-4 h-4 mr-2" />
              Tout marquer comme lu
            </Button>
          )}
        </div>

        {/* Notifications */}
        {notifications.length === 0 ? (
          <Card className="card-premium">
            <CardContent className="py-12 text-center">
              <Bell className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Aucune notification</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {notifications.map(notification => (
              <Card 
                key={notification.id} 
                className={`card-premium transition-colors ${!notification.is_read ? 'border-l-2 border-l-[#D4AF37]' : ''} ${notification.notification_type === 'important' && !notification.is_read ? 'bg-amber-500/5' : ''}`}
                data-testid={`notification-${notification.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 mt-1">
                      {getIcon(notification.notification_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className={`font-medium ${!notification.is_read ? 'text-foreground' : 'text-muted-foreground'}`}>
                          {notification.title}
                        </h3>
                        <Badge className={
                          notification.notification_type === 'critical' ? 'bg-red-500/20 text-red-500 border border-red-500/30' :
                          notification.notification_type === 'important' ? 'bg-amber-500/20 text-amber-500 border border-amber-500/30 animate-pulse' :
                          notification.notification_type === 'warning' ? 'bg-orange-500/20 text-orange-500' :
                          notification.notification_type === 'success' ? 'bg-emerald-500/20 text-emerald-400' :
                          'bg-blue-500/20 text-blue-400'
                        }>
                          {notification.notification_type === 'critical' ? 'URGENT' :
                           notification.notification_type === 'important' ? 'IMPORTANT' :
                           notification.notification_type === 'warning' ? 'Attention' :
                           notification.notification_type === 'success' ? 'Succès' :
                           'Info'}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{notification.message}</p>
                      <div className="flex items-center justify-between mt-3">
                        <span className="text-xs text-muted-foreground">
                          {notification.created_at ? `${new Date(notification.created_at).toLocaleDateString()} à ${new Date(notification.created_at).toLocaleTimeString()}` : ''}
                        </span>
                        <div className="flex gap-2">
                          {notification.link && (
                            <Link to={cleanNotificationLink(notification.link)}>
                              <Button size="sm" variant="outline">
                                Voir
                              </Button>
                            </Link>
                          )}
                          {!notification.is_read && (
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => handleMarkAsRead(notification.id)}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                          )}
                          {canDelete && (
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                            onClick={async () => {
                              try {
                                await axios.delete(`${API}/notifications/${notification.id}`, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                setNotifications(prev => prev.filter(n => n.id !== notification.id));
                                toast.success('Notification supprimée');
                                window.dispatchEvent(new Event('notifications-updated'));
                              } catch { toast.error('Erreur'); }
                            }}
                            data-testid={`delete-notif-${notification.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
